// 마을 상호작용: 거리 안내와 명시적인 창 열기, NPC 몸 충돌을 한 곳에서 판정한다.
import { HERO } from '../shared/config';
import { Ev } from '../shared/events';
import { npcDef, townNpcs } from '../shared/rpg';
import { Btn, Scene, type InputFrame, type TxCode, type VillageView } from '../shared/types';
import type { GameSim } from './game';
import type { ProfileCommand } from './profile';
import type { GrowthCode } from '../shared/growth';
import type { GearCode } from '../shared/gearLedger';
import type { GearCmdKind } from '../shared/gearView';
import { REBIRTH_GATE, type RebirthCode } from '../shared/rebirth';

export class VillageController implements VillageView {
  nearNpc = -1;
  menuNpc = -1;
  inventoryOpen = false;
  stageMapOpen = false;
  questOpen = false;
  /** 윤회의 문(레인 R): 반경 안 / 확인 화면 열림 */
  nearGate = false;
  rebirthOpen = false;
  constructor(private readonly g: GameSim) {}
  get open(): boolean { return this.menuNpc >= 0 || this.inventoryOpen || this.stageMapOpen || this.questOpen || this.rebirthOpen; }

  updateNear(): void {
    this.nearNpc = -1;
    if (this.g.flow.scene !== Scene.Village) return;
    const h = this.g.hero;
    let closest = Infinity;
    // 그 마을에 선 NPC만(S7: 마을마다 자리가 다르고 보석상 ③·연금술사 ④만 있다). nearNpc = ALL_NPCS 인덱스
    const list = townNpcs(this.g.flow.town);
    for (let i = 0; i < list.length; i++) {
      const npc = list[i], dx = h.x - npc.x, dz = h.z - npc.z, d = dx * dx + dz * dz;
      if (d <= npc.interactRadius * npc.interactRadius && d < closest) { closest = d; this.nearNpc = npc.index; }
    }
    // 윤회의 문: 보일 때만(마을①·Lv90+·다음 모드 열림), NPC가 더 가까우면 NPC가 먼저
    const gx = h.x - REBIRTH_GATE.x, gz = h.z - REBIRTH_GATE.z;
    this.nearGate = this.nearNpc < 0 && gx * gx + gz * gz <= REBIRTH_GATE.interactRadius * REBIRTH_GATE.interactRadius && this.g.rebirth.gateVisible();
  }

  interact(input: InputFrame): void {
    this.updateNear();
    if (this.g.flow.scene !== Scene.Village || this.g.flow.paused || this.open || !(input.pressed & Btn.Interact)) return;
    if (this.nearNpc >= 0) this.menuNpc = this.nearNpc;
    else if (this.nearGate) this.rebirthOpen = true;
    else return;
    this.opened();
  }

  openInventory(): void {
    if (this.g.flow.scene !== Scene.Village || this.g.flow.paused || this.open) return;
    this.inventoryOpen = true;
    this.opened();
  }

  /** 스테이지 지도(S3): 마을에서만, 다른 창이 없을 때. 지도 안에서 입장하면 판이 바뀌며 reset()이 닫는다 */
  openStageMap(): void {
    if (this.g.flow.scene !== Scene.Village || this.g.flow.paused || this.open || this.g.flow.travelTo > 0) return;
    this.stageMapOpen = true;
    this.opened();
  }

  /** 퀘스트 창: 마을에서만, 다른 창이 없을 때(전투 중인 던전·일시정지·이동 연출 중에는 열리지 않는다) */
  openQuest(): void {
    if (this.g.flow.scene !== Scene.Village || this.g.flow.paused || this.open || this.g.flow.travelTo > 0) return;
    this.questOpen = true;
    this.opened();
  }

  private opened(): void {
    this.g.suppressHeroInput();
    this.g.heroController.stopForMenu();
    const e = this.g.emit(Ev.Menu); e.a = 1; e.b = this.menuNpc;
  }

  closeMenu(): void {
    if (!this.open) return;
    const e = this.g.emit(Ev.Menu); e.a = 0; e.b = this.menuNpc;
    this.menuNpc = -1; this.inventoryOpen = false; this.stageMapOpen = false; this.questOpen = false; this.rebirthOpen = false;
  }

  reset(): void { this.closeMenu(); this.nearNpc = -1; this.nearGate = false; }

  /**
   * 환생 명령 문맥(레인 R): 환생 = 마을에서 윤회의 문 확인 화면이 열린 동안. 배분 = 교관 창·윤회의 문 창, 또는 메뉴 줄 「스킬」/N 키로 연 스킬 서랍(장비 창 inventoryOpen). 소개 카드 확인 = 마을 어디서나.
   * 일시정지는 거절. 정상이면 undefined
   */
  rebirthDenial(kind: 'rebirth' | 'rebirthAlloc' | 'rebirthIntro'): RebirthCode | undefined {
    const f = this.g.flow;
    if (f.paused) return 'paused';
    if (f.scene !== Scene.Village) return 'no_menu';
    if (kind === 'rebirthIntro') return undefined;
    if (kind === 'rebirth') return this.rebirthOpen ? undefined : 'no_menu';
    return this.rebirthOpen || this.inventoryOpen || npcDef(this.menuNpc)?.role === 'trainer' ? undefined : 'no_menu';
  }

  denial(cmd: ProfileCommand): TxCode | undefined {
    const f = this.g.flow;
    if (cmd.type === 'recoverProfile') return f.scene === Scene.Village ? undefined : 'readonly';
    if (f.paused) return 'paused';
    if (f.scene !== Scene.Village || !this.open) return 'no_menu';
    if (cmd.type === 'buy') {
      if (this.menuNpc < 0) return 'no_menu';
      // 장비 통합(ECONOMY 14절): 옛 상점 무기·갑옷은 더 팔지 않는다(6부위 장비 상점 gearBuy가 대신한다)
      return 'unknown_item';
    } else if (cmd.type === 'setSkillFocus' && npcDef(this.menuNpc)?.role !== 'trainer') return 'no_menu';
    return undefined;
  }

  /**
   * 스킬 트리 배우기·초기화 문맥 거절. 마을에서 장비 창(inventoryOpen)이나 교관 창이 열려 있어야 한다.
   * 일시정지는 따로 막는다. 정상이면 undefined.
   */
  skillDenial(): GrowthCode | undefined {
    const f = this.g.flow;
    if (f.paused) return 'paused';
    if (f.scene !== Scene.Village || !this.open) return 'no_menu';
    if (this.inventoryOpen) return undefined;
    if (npcDef(this.menuNpc)?.role === 'trainer') return undefined;
    return 'no_menu';
  }

  /**
   * 장비 명령 문맥 거절(D2a·D2b). 장착·벗기·정리: 마을 가방 창 또는 대장간 창. 강화·보호 주문서 구매: 대장간 창만(PRD 4.3 대장간 강화).
   * 일시정지·마을 밖·창 닫힘은 거절. 정상이면 undefined.
   */
  gearDenial(kind: GearCmdKind): GearCode | undefined {
    const f = this.g.flow;
    if (f.paused || f.scene !== Scene.Village || !this.open) return 'denied';
    const role = npcDef(this.menuNpc)?.role;
    const forge = role === 'forge', shop = role === 'shop';
    if (kind === 'enhance' || kind === 'buyProtect') return forge ? undefined : 'denied';
    // D2c: 물약·주문서 구매와 비약 제작은 잡화상 창, 보석 박기·빼기·합성은 대장간 창. S7: 연금술사(마을④)·보석상(마을③) 창도 같은 일을 한다
    if (kind === 'buy' || kind === 'craft') return shop || role === 'alchemist' ? undefined : 'denied';
    if (kind === 'socket' || kind === 'unsocket' || kind === 'combine') return forge || role === 'jeweler' ? undefined : 'denied';
    // S7 마을 장비 상점: 그 장비를 파는 NPC 창(game.ts가 품목의 판매 NPC·마을을 한 번 더 본다)
    if (kind === 'buyGear') return shop || forge ? undefined : 'denied';
    return this.inventoryOpen || forge ? undefined : 'denied';
  }

  blocks(x: number, z: number): boolean {
    if (this.g.flow.scene !== Scene.Village) return false;
    const list = townNpcs(this.g.flow.town);
    for (let i = 0; i < list.length; i++) {
      const n = list[i], dx = x - n.x, dz = z - n.z, radius = HERO.radius + n.radius;
      if (dx * dx + dz * dz < radius * radius) return true;
    }
    return false;
  }
}
